import { COBRANCAS_DO_EXTRATO, COBRANCA_LABELS, DIAS_DA_SEMANA, FREQUENCIAS_DO_EXTRATO, FREQUENCIA_LABELS, type RegraDeExtrato } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { campo, rotulo } from "../estilos";

type Valor<P extends boolean> = P extends true ? { [K in keyof RegraDeExtrato]: RegraDeExtrato[K] | null } : RegraDeExtrato;

const DIAS = Array.from({ length: 31 }, (_, i) => i + 1);

function Botoes<T extends string>({ opcoes, valor, onChange, rotulos, comPadrao }: { opcoes: readonly T[]; valor: T | null; onChange: (v: T | null) => void; rotulos: Record<T, string>; comPadrao: boolean }) {
  const todas: (T | null)[] = comPadrao ? [null, ...opcoes] : [...opcoes];
  return (
    <div className="flex flex-wrap gap-1.5">
      {todas.map((o) => (
        <button
          key={o ?? "padrao"}
          type="button"
          aria-pressed={valor === o}
          onClick={() => onChange(o)}
          className={cn(
            "h-9 rounded-md border px-3 text-sm font-medium transition-colors",
            valor === o
              ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
              : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
          )}
        >
          {o ? rotulos[o] : "Padrão"}
        </button>
      ))}
    </div>
  );
}

/** Quem paga, de quanto em quanto tempo e o vencimento — o padrão do laboratório (`comPadrao`
 *  falso) ou a regra de um cliente, onde cada campo pode ficar "Padrão" (nulo). */
export function CamposDaCobranca<P extends boolean>({ valor, onChange, comPadrao, padrao }: { valor: Valor<P>; onChange: (v: Valor<P>) => void; comPadrao: P; padrao?: RegraDeExtrato }) {
  const mudar = (parcial: Partial<Valor<P>>) => onChange({ ...valor, ...parcial } as Valor<P>);
  const frequencia = valor.frequencia ?? padrao?.frequencia;
  return (
    <div className="space-y-3">
      <div>
        <p className={rotulo}>Cobrar</p>
        <Botoes opcoes={COBRANCAS_DO_EXTRATO} rotulos={COBRANCA_LABELS} valor={valor.cobranca} onChange={(cobranca) => mudar({ cobranca } as Partial<Valor<P>>)} comPadrao={comPadrao} />
      </div>
      <div>
        <p className={rotulo}>Frequência</p>
        <Botoes opcoes={FREQUENCIAS_DO_EXTRATO} rotulos={FREQUENCIA_LABELS} valor={valor.frequencia} onChange={(f) => mudar({ frequencia: f } as Partial<Valor<P>>)} comPadrao={comPadrao} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {frequencia === "MENSAL" && (
          <label>
            <span className={rotulo}>Dia de fechamento</span>
            <select
              value={valor.diaDeFechamento ?? ""}
              onChange={(e) => mudar({ diaDeFechamento: e.target.value ? Number(e.target.value) : null } as Partial<Valor<P>>)}
              className={campo}
            >
              {comPadrao && <option value="">Padrão</option>}
              {DIAS.map((d) => (
                <option key={d} value={d}>
                  {d === 31 ? "Último dia do mês" : `Dia ${d}`}
                </option>
              ))}
            </select>
          </label>
        )}
        {frequencia === "SEMANAL" && (
          <label>
            <span className={rotulo}>Fecha na</span>
            <select
              value={valor.diaDaSemana ?? ""}
              onChange={(e) => mudar({ diaDaSemana: e.target.value ? Number(e.target.value) : null } as Partial<Valor<P>>)}
              className={campo}
            >
              {comPadrao && <option value="">Padrão</option>}
              {DIAS_DA_SEMANA.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <span className={rotulo}>Vencimento (dias depois de emitir)</span>
          <input
            type="number"
            min={0}
            max={120}
            value={valor.prazoDeVencimentoDias ?? ""}
            placeholder={comPadrao ? `Padrão (${padrao?.prazoDeVencimentoDias ?? ""})` : undefined}
            onChange={(e) => mudar({ prazoDeVencimentoDias: e.target.value === "" ? null : Math.min(Math.max(Number(e.target.value), 0), 120) } as Partial<Valor<P>>)}
            className={campo}
          />
        </label>
      </div>
    </div>
  );
}
